import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Avatar from '@decisiv/ui-components/lib/components/Avatar';
import Modal from '@decisiv/ui-components/lib/components/Modal';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import iconix from '@decisiv/iconix';

import AccountsField from '../../shared/AccountsField';
import { ACCESS_MODAL } from './accessCopy';
import { ContactCard, ContactDetails, ModalBody } from './styles';
import { REMOVE } from '../hooks/useUserAccess';

/**
 * Remove Access / Restore Access from a Users list row (Figma 1301:21860,
 * 1301:21712).
 *
 * One component for both, the way the Access & Permissions tab's
 * RemoveRestoreModal covers its pair: the two differ in wording, icon and
 * intent, and nothing else. Splitting them would duplicate the account picker
 * and the failure handling.
 *
 * A failure renders inline and leaves the dialog open, so the admin can narrow
 * the selection and try again without rebuilding it; success is a toast raised
 * by the caller, which is the split the design draws.
 *
 * The design's Team select above the picker is not built — see accessCopy.
 */
function AccessModal({
  accountOptions,
  action,
  error,
  isLoading,
  isSubmitting,
  onChangeSelected,
  onClose,
  onSubmit,
  selectedIds,
  user,
  visible,
}) {
  const [search, setSearch] = useState('');

  // This component stays mounted between openings, so a term left behind would
  // still be filtering the picker the next time it opens — against a different
  // user's accounts, which can leave it looking empty.
  useEffect(() => {
    if (visible) setSearch('');
  }, [visible, action, user?.id]);

  const isRemove = action === REMOVE;
  const copy = ACCESS_MODAL[action] || ACCESS_MODAL.remove;

  // Filtered here rather than server-side: the list is the accounts of one
  // user, already in hand, so a request per keystroke would buy nothing.
  const options = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return accountOptions;

    return accountOptions.filter((option) =>
      option.label.toLowerCase().includes(term),
    );
  }, [accountOptions, search]);

  const hasAccounts = accountOptions.length > 0;
  const canSubmit = !isLoading && hasAccounts && selectedIds.length > 0;

  return (
    <Modal
      actions={[
        {
          text: copy.submit,
          onClick: onSubmit,
          kind: 'primary',
          loading: isSubmitting,
          loadingText: copy.submitting,
          disabled: !canSubmit,
          ...(isRemove && { intent: 'danger' }),
        },
        {
          text: ACCESS_MODAL.cancel,
          onClick: onClose,
          kind: 'secondary',
          disabled: isSubmitting,
        },
      ]}
      intent={isRemove ? 'danger' : 'information'}
      icon={isRemove ? iconix.UserTimes : iconix.UserPlus}
      onClose={onClose}
      title={copy.title}
      visible={visible}
    >
      <ModalBody>
        <P shade={1}>{copy.description}</P>

        {user && (
          <ContactCard>
            <Avatar email={user.email} name={user.name} />
            <ContactDetails>
              <P weight="semibold" style={{ margin: 0 }}>
                {user.jobRole ? `${user.name} (${user.jobRole})` : user.name}
              </P>
              <P shade={1} size="small" style={{ margin: 0 }}>
                {user.email}
              </P>
            </ContactDetails>
          </ContactCard>
        )}

        {isLoading && <P shade={1}>{ACCESS_MODAL.loading}</P>}

        {!isLoading && !hasAccounts && <P shade={1}>{copy.accountEmpty}</P>}

        {!isLoading && hasAccounts && (
          <AccountsField
            help={copy.accountHelp}
            inputId="user-access-accounts"
            label={copy.accountLabel}
            listId="user-access-accounts-list"
            onChange={onChangeSelected}
            onRemove={(value) =>
              onChangeSelected(selectedIds.filter((id) => id !== value))
            }
            onSearch={setSearch}
            options={options}
            selected={selectedIds}
            total={accountOptions.length}
            // Above the Modal's own layer, or the panel opens behind it.
            zIndex={1100}
          />
        )}

        {error && (
          <div role="alert">
            <Alert
              intent="danger"
              title={copy.failedTitle}
              description={error}
            />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}

AccessModal.propTypes = {
  accountOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  action: PropTypes.oneOf(['remove', 'restore']),
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onChangeSelected: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectedIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  user: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    email: PropTypes.string,
    jobRole: PropTypes.string,
  }),
  visible: PropTypes.bool,
};

AccessModal.defaultProps = {
  accountOptions: [],
  action: null,
  error: null,
  isLoading: false,
  isSubmitting: false,
  selectedIds: [],
  user: null,
  visible: false,
};

export default AccessModal;
